import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import { Box, Flex } from '@mantine/core';
import { ErrorsWithFundsStyle } from '../../FCA/components/ReportTables/style';
import commonStore from '../../../../store/common';
import { idCreator } from '../../../Project/utils/idCreator';

/**
 * Reusable component to display errors with funds in report tables
 * @param {Object} props
 * @param {Object} props.funds - Object containing fund data keyed by fund ID
 * @param {Array} props.errorsWithFunds - Array of errors associated with funds
 * @param {number} props.columnMinWidthOffset - Offset to add to column minimum width calculation (default: 40)
 * @param {Object} props.flexStyles - Optional additional styles for the Flex container
 */

interface ErrorItem {
  msg: string;
}

interface ErrorWithFund {
  fund_id: string;
  name: string;
  isRequiredTable?: boolean;
  errors?: ErrorItem[];
}

interface FundErrorsDisplayProps {
  funds: Record<string, { fund_name: string }>;
  errorsWithFunds: Array<{
    fund_id: string;
    name: string;
    isRequiredTable?: boolean;
    errors?: Array<{ msg: string }>;
  }>;
  columnWidth?: number;
  columnMinWidthOffset?: number;
}
export const FundErrorsDisplay = ({
  funds,
  errorsWithFunds,
  columnWidth = 240,
  columnMinWidthOffset = 40
}: FundErrorsDisplayProps) => {
  const contentWidth = useSyncExternalStore(
    onStoreChange => reaction(() => commonStore.contentWidth, onStoreChange),
    () => commonStore.contentWidth
  );

  const fundSize: number = Object.keys(funds).length;
  const columnMinWidth: number =
    fundSize <= 3 ? contentWidth / (fundSize + 1) + columnMinWidthOffset : columnWidth;

  const calculateFundWidth = (): number | undefined => {
    return fundSize <= 3 ? contentWidth / (fundSize + 1) : undefined;
  };

  return (
    <ErrorsWithFundsStyle id="fund-errors-display">
      <div
        className="column"
        id="fund-errors-display-column-spacer"
        style={{
          minWidth: columnMinWidth
        }}
      />
      <Flex id="fund-errors-display-funds" align="center" flex="auto">
        {Object.keys(funds).map((fundId: string) => {
          const value: ErrorWithFund | undefined = errorsWithFunds.find(
            (val: ErrorWithFund) => val.fund_id === fundId
          );
          const fundWidth: number | undefined = calculateFundWidth();

          if (value) {
            return (
              <Box
                key={fundId}
                className="element"
                id={`fund-errors-display-item-${fundId}`}
                miw={fundWidth}
                maw={fundWidth}
              >
                <p id={`fund-errors-display-name-${fundId}`} title={value.name}>
                  {value.name}
                </p>
                <ul id={`fund-errors-display-list-${fundId}`}>
                  {value?.isRequiredTable && (
                    <li id={`fund-errors-display-required-${fundId}`}>
                      This section must be completed
                    </li>
                  )}
                  {value?.errors?.map((item: ErrorItem) => (
                    <li
                      key={item.msg}
                      id={`fund-errors-display-error-${fundId}-${idCreator(item.msg)}`}
                    >
                      {item.msg}
                    </li>
                  ))}
                </ul>
              </Box>
            );
          }

          return (
            <Box
              id={`fund-errors-display-fund-${fundId}`}
              key={fundId}
              className="element"
              miw={fundWidth}
              maw={fundWidth}
              bg="#fff"
              h="100%"
              // @ts-ignore
              sx={() => ({
                border: 'none'
              })}
            >
              <p id={`fund-errors-display-fund-name-${fundId}`}>{funds[fundId].fund_name}</p>
            </Box>
          );
        })}
      </Flex>
    </ErrorsWithFundsStyle>
  );
};
